import { SafeError } from './safeAwait';
import { emitOutboundFetchError } from './outboundFetchErrorLogger';
import { getEventLoopDelaySnapshot } from './outboundRequestEventLoopDelay';

export type HttpErrorLogContext = {
  body?: string;
  method: string;
  url: string;
};

const CONNECTION_ERROR_CODES = [
  'ECONNREFUSED',
  'ECONNRESET',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_SOCKET',
  'ETIMEDOUT',
  'EHOSTUNREACH',
];

const shouldLogOutboundDiagnostics = (): boolean =>
  typeof window === 'undefined' && process.env.BZ_UPSTREAM_REQUEST_STATS === 'true';

function logOutboundDiagnostic(event: string, payload: Record<string, unknown>): void {
  console.warn(
    JSON.stringify({
      event,
      level: 'warn',
      timestamp: new Date().toISOString(),
      ...payload,
    }),
  );
}

function readSafeErrorStatus(safeError: SafeError): number | undefined {
  const status = (safeError.data as { status?: number } | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
}

export const logHttpError = (context: HttpErrorLogContext, err: unknown): void => {
  if (typeof window !== 'undefined') return;

  const { body, method, url } = context;
  const safeError = err as SafeError;

  if (safeError instanceof SafeError && safeError.type === 'outbound_request_circuit_open') {
    if (shouldLogOutboundDiagnostics()) {
      const data = (safeError.data ?? {}) as Record<string, unknown>;
      logOutboundDiagnostic('outbound_request_circuit_open_detail', {
        errorType: safeError.type,
        label: `[HTTP ERROR] ${method}: ${url}`,
        message: safeError.message,
        outboundRequestPolicy: data.outboundRequestPolicy,
        status: data.status,
      });
    }
    emitOutboundFetchError({
      body,
      errorType: safeError.type,
      level: 'warn',
      method,
      statusCode: 503,
      url,
    });
    return;
  }

  if (safeError instanceof SafeError && safeError.type === 'timeout') {
    emitOutboundFetchError({
      body,
      errorType: safeError.type,
      level: 'warn',
      method,
      statusCode: 504,
      url,
    });
    if (shouldLogOutboundDiagnostics()) {
      const data = (safeError.data ?? {}) as Record<string, unknown>;
      logOutboundDiagnostic('outbound_request_timeout_diagnostics', {
        activeGlobalAtAcquire: data.activeGlobalAtAcquire,
        activeHostAtAcquire: data.activeHostAtAcquire,
        errorType: safeError.type,
        eventLoopDelay: getEventLoopDelaySnapshot(),
        fetchDurationMs: data.fetchDurationMs,
        host: data.host,
        label: `[HTTP ERROR] ${method}: ${url}`,
        queueDepthAtAcquire: data.queueDepthAtAcquire,
        queueWaitMs: data.queueWaitMs,
        timeoutBudgetMs: data.timeoutBudgetMs,
      });
    }
    return;
  }

  if (safeError instanceof SafeError && safeError.data) {
    const errorData = safeError.data as { cause?: { code?: string } };
    const errorCode = errorData.cause?.code ?? '';
    if (safeError.type === 'fetch failed' && CONNECTION_ERROR_CODES.includes(errorCode)) {
      emitOutboundFetchError({
        body,
        errorCode,
        errorType: safeError.type,
        level: 'warn',
        method,
        url,
      });
      return;
    }
    if (safeError.type === 'maxRetries') {
      emitOutboundFetchError({
        body,
        errorCode,
        errorType: safeError.type,
        level: 'warn',
        method,
        url,
      });
      return;
    }
  }

  if (safeError instanceof SafeError) {
    emitOutboundFetchError({
      body,
      errorType: safeError.type,
      level: 'error',
      method,
      statusCode: readSafeErrorStatus(safeError),
      url,
    });
    return;
  }

  const errorMessage = err instanceof Error ? err.message : err != null ? String(err) : undefined;
  if (errorMessage) {
    emitOutboundFetchError({
      body,
      errorType: 'unknown',
      level: 'error',
      method,
      url,
    });
  }
};

export const logHttpResponseError = (method: string, url: string, statusCode: number): void => {
  if (typeof window !== 'undefined') return;
  if (statusCode < 500) return;

  emitOutboundFetchError({
    errorType: 'http_response_error',
    level: 'warn',
    method,
    statusCode,
    url,
  });
};
